// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - TOKEN DATA API
// Fetches price + icon via /api/tokens proxy (DexScreener, no key needed).
// Batches up to 30 tokens per call, 30s cache.
// ═══════════════════════════════════════════════════════════════════════════

const CACHE_DURATION = 30000; // 30 seconds
const BATCH_SIZE = 29;

const cache = {
  tokenMap: {},  // { coinType: { price, icon, name, symbol } }
  timestamp: null,
};

function isCacheValid() {
  return cache.timestamp && Date.now() - cache.timestamp < CACHE_DURATION;
}

/**
 * Fetch price + icon for a list of coin types.
 * Returns { priceMap, iconMap } keyed by coinType.
 */
export async function fetchTokenData(coinTypes) {
  if (!coinTypes || coinTypes.length === 0) return { priceMap: {}, iconMap: {} };

  if (isCacheValid() && Object.keys(cache.tokenMap).length > 0) {
    console.log('💰 [DS] Using cached token data');
    return buildMaps(cache.tokenMap);
  }

  const unique = [...new Set(coinTypes)];
  const batches = [];
  for (let i = 0; i < unique.length; i += BATCH_SIZE) {
    batches.push(unique.slice(i, i + BATCH_SIZE));
  }

  console.log(`📡 [DS] Fetching data for ${unique.length} tokens in ${batches.length} batch(es)...`);

  const tokenMap = {};

  await Promise.all(batches.map(async (batch) => {
    const addresses = batch.join(',');
    try {
      const res = await fetch(`/api/tokens?addresses=${encodeURIComponent(addresses)}`);
      if (!res.ok) {
        console.warn(`⚠️ [DS] Batch failed: ${res.status}`);
        return;
      }
      const data = await res.json();
      const tokens = data.tokens || {};

      // Match returned addresses back to original coinTypes (case-insensitive)
      batch.forEach(coinType => {
        // DexScreener uses the contract address portion of the coin type
        // Try matching by the full coinType or by the address prefix
        const addr = coinType.split('::')[0].toLowerCase();
        
        // Find matching key in returned tokens
        const matchedKey = Object.keys(tokens).find(k => 
          k.toLowerCase() === coinType.toLowerCase() ||
          k.toLowerCase() === addr
        );

        if (matchedKey) {
          tokenMap[coinType] = tokens[matchedKey];
        }
      });
    } catch (err) {
      console.warn(`⚠️ [DS] Batch error:`, err.message);
    }
  }));

  cache.tokenMap = tokenMap;
  cache.timestamp = Date.now();

  const priceCount = Object.values(tokenMap).filter(t => t.price != null).length;
  const iconCount = Object.values(tokenMap).filter(t => t.icon != null).length;
  console.log(`✅ [DS] Got ${priceCount} prices, ${iconCount} icons for ${unique.length} tokens`);

  return buildMaps(tokenMap);
}

function buildMaps(tokenMap) {
  const priceMap = {};
  const iconMap = {};
  Object.entries(tokenMap).forEach(([coinType, data]) => {
    if (data.price != null) priceMap[coinType] = data.price;
    if (data.icon) iconMap[coinType] = data.icon;
  });
  return { priceMap, iconMap };
}

/**
 * Legacy alias — used by existing App.jsx price refresh
 */
export async function fetchPricesForTokens(coinTypes) {
  const { priceMap } = await fetchTokenData(coinTypes);
  return priceMap;
}

export function clearPriceCache() {
  cache.tokenMap = {};
  cache.timestamp = null;
  console.log('🗑️ [DS] Cache cleared');
}

export function getCachedPrice(coinType) {
  return cache.tokenMap[coinType]?.price || null;
}

export function getCachedIcon(coinType) {
  return cache.tokenMap[coinType]?.icon || null;
}

export default { fetchTokenData, fetchPricesForTokens, clearPriceCache, getCachedPrice, getCachedIcon };
